import jsPDF from "jspdf"
import { blue, yellow } from "@mui/material/colors"
import { numberFormat, numberFormatNoFlotingPoint } from "utils"
import { printHeader } from "../../functions/pirntHeader"
import { printDriverSalaryTopRight } from "../DriverSalary/printDriverSalaryTopRight"
import { buildSlipData, slipDetailRows, slipTotalRows, OTH_DEDUCT_EXP_ID } from "./slipData"

// Payslip layout: 3 slips per A4 page, fed from the V3 pc_item model — the รายได้ / รายการหัก
// boxes are rendered dynamically from the actual income / deduction lines (labels from ExpNm)
// instead of a fixed set of named slots, so a new salary expense needs no change here.

// Rows a box can hold before overflowing its ruled area.
const MAX_BOX_ROWS = 10

// Keep every row that carries a real amount: when a box overflows, drop its zero rows (from the
// bottom up) rather than silently truncating the tail — an amount must never be hidden.
const fitBoxRows = (rows) => {
  if (rows.length <= MAX_BOX_ROWS) return rows
  const kept = [...rows]
  for (let i = kept.length - 1; i >= 0 && kept.length > MAX_BOX_ROWS; i--) {
    if (!kept[i].isDetail && !kept[i].keep && !+(kept[i].amnt || 0)) kept.splice(i, 1)
  }
  return kept.slice(0, MAX_BOX_ROWS)
}

// สรุปยอด rows plus the หักอื่นๆ หมายเหตุ, padded with spacers so the note prints on the same
// line as the หักอื่นๆ row of the รายการหัก box beside it (deductRows = that box's fitted rows).
const totalRowsWithDscp = (data, deductRows) => {
  const rows = slipTotalRows(data)
  if (!data.OthDeDscp) return rows
  const idx = deductRows.findIndex(r => r.ExpId === OTH_DEDUCT_EXP_ID)
  if (idx < 0) return rows
  while (rows.length < idx) rows.push({ isBlank: true, keep: true })
  rows.push({ isText: true, keep: true, label: data.OthDeDscp })
  return rows
}

// exported so printDriverSalaryV3Detail can draw the identical four-box block above its
// ค่าเที่ยว detail table — the two reports must never drift apart
export const drawSalaryBox = ({ doc, startX, startY, data }) => {
  const lineHeight = 0.18
  const numberColWidth = 1.65
  const gap = 0.2
  const summaryTableWidth = numberColWidth + gap
  const tableSpace = 0.15
  const startTableX = startX + 0.05

  const drawSummaryTable = (tableStartX, tableStartY) => {
    let tableYPos = tableStartY - lineHeight
    let tableXPos = tableStartX - 0.1
    doc.setFillColor(blue[100])
    doc.rect(tableXPos, tableYPos, summaryTableWidth, lineHeight + 0.1, "F")
    doc.line(tableXPos, tableYPos, tableXPos + summaryTableWidth, tableYPos)
    tableYPos += lineHeight + 0.1
    doc.line(tableXPos, tableYPos, tableXPos + summaryTableWidth, tableYPos)
    tableYPos += (lineHeight * MAX_BOX_ROWS) + 0.1
    doc.setFillColor(yellow[100])
    doc.rect(tableXPos, tableYPos, summaryTableWidth, lineHeight + 0.1, "F")
    doc.line(tableXPos, tableYPos, tableXPos + summaryTableWidth, tableYPos)
    tableYPos += lineHeight + 0.1
    doc.line(tableXPos, tableYPos, tableXPos + summaryTableWidth, tableYPos)
    doc.line(tableXPos, tableStartY - lineHeight, tableXPos, tableYPos)
    doc.line(tableXPos + summaryTableWidth, tableStartY - lineHeight, tableXPos + summaryTableWidth, tableYPos)
  }

  const drawAmnt = (label, amnt, xPos, yPos, isMinus) => {
    doc.text(label || "-", xPos, yPos)
    doc.text(`${isMinus ? "- " : ""}${numberFormat(+(amnt || 0))} บาท`, xPos + numberColWidth, yPos, { align: "right" })
  }
  // one line of free text, shortened to the column width (box rows are fixed height — no wrapping)
  const clipText = (text) => {
    let t = String(text)
    if (doc.getTextWidth(t) <= numberColWidth + gap) return t
    while (t.length > 1 && doc.getTextWidth(t + "…") > numberColWidth + gap) t = t.slice(0, -1)
    return t + "…"
  }
  const drawDetail = (label, unit, value, xPos, yPos) => {
    doc.text(label || "-", xPos, yPos)
    doc.text(`${value ? numberFormatNoFlotingPoint(+value) : "-"} ${unit}`, xPos + numberColWidth, yPos, { align: "right" })
  }

  // draws one titled box: header, up to MAX_BOX_ROWS data rows, then a bold footer total
  const drawBox = (boxStartX, title, rows, footerLabel, footerAmnt, footerMinus) => {
    const startYData = startY
    let xPos = boxStartX
    let yPos = startYData
    drawSummaryTable(xPos, yPos)
    doc.setFont("THSarabun", "bold")
    doc.text(title, xPos + numberColWidth / 2, yPos, { align: "center" })
    doc.setFont("THSarabun", "normal")
    yPos += lineHeight + 0.1
    for (const r of fitBoxRows(rows)) {
      if (r.isBlank) { /* spacer — keeps the next row level with the box beside it */ }
      else if (r.isText) doc.text(clipText(r.label || ""), xPos, yPos)
      else if (r.isDetail) drawDetail(r.label, r.unit, r.value, xPos, yPos)
      else drawAmnt(r.label, r.amnt, xPos, yPos, r.minus)
      yPos += lineHeight
    }
    if (footerLabel != null) {
      yPos = startYData + (lineHeight * (MAX_BOX_ROWS + 1)) + 0.2
      doc.setFont("THSarabun", "bold")
      doc.text(footerLabel, xPos, yPos)
      doc.text(`${footerMinus ? "- " : ""}${numberFormat(+(footerAmnt || 0))} บาท`, xPos + numberColWidth, yPos, { align: "right" })
      doc.setFont("THSarabun", "normal")
    }
    return startYData + (lineHeight * (MAX_BOX_ROWS + 1)) + 0.2
  }

  // 1) รายได้   2) รายการหัก   3) สรุปยอด   4) ข้อมูลสรุป
  let boxX = startTableX
  const bottomY = drawBox(boxX, "รายได้", data.incomeRows, "รวมรายได้", data.SumInc)

  boxX += summaryTableWidth + tableSpace
  // fit the deduction rows here so สรุปยอด can align its note to the printed หักอื่นๆ row
  const deductRows = fitBoxRows(data.deductRows)
  drawBox(boxX, "รายการหัก", deductRows, "รวมรายการหัก", data.SumDeduct)

  boxX += summaryTableWidth + tableSpace
  drawBox(boxX, "สรุปยอด", totalRowsWithDscp(data, deductRows), "ยอดคงเหลือ", data.Net)

  boxX += summaryTableWidth + tableSpace
  drawBox(boxX, "ข้อมูลสรุป", slipDetailRows(data), null)

  return bottomY + lineHeight
}

export const printDriverSalaryV3Summary = ({ msData, payrolls, expColumns = [] }) => {
  const pageSize = [8.3, 11.7]
  const sectionHeight = 11.7 / 3
  const doc = new jsPDF({ orientation: "portrait", unit: "in", format: pageSize })

  const startY = 0.2
  const startX = 0.3
  const lineHeight = 0.21
  const contentSize = 14
  const MAX_ROW = 3

  doc.setLineWidth(0.01)
  doc.setFont("THSarabun")

  const slips = payrolls.map(p => buildSlipData(p, expColumns))
  const numPage = Math.ceil(slips.length / MAX_ROW)

  for (let i = 0; i < numPage; i++) {
    if (i > 0) doc.addPage(pageSize)
    const pageSlips = slips.slice(i * MAX_ROW, (i + 1) * MAX_ROW)

    let j = 0
    for (const data of pageSlips) {
      let yPos = startY + (j * sectionHeight)
      const xPos = startX

      const yPosTopRight = printDriverSalaryTopRight({ doc, dialogData: data, startY: yPos + 0.1, lineHeight, contentSize, page: 0, totalPage: 0 })
      const yPosHeader = printHeader({ jsPDFDoc: doc, companyData: msData.serverData.CompanyData, startX: xPos, startY: yPos })
      yPos = Math.max(yPosTopRight, yPosHeader) + lineHeight + lineHeight / 2 // one line under the company header
      drawSalaryBox({ doc, startX, startY: yPos, data })
      j++
    }

    doc.setLineDashPattern([0.5, 3.2, 0.9, 3.2, 0.5])
    let lineY = 0
    for (let k = 0; k < MAX_ROW; k++) {
      lineY += sectionHeight
      doc.line(0, lineY, 8.3, lineY)
    }
    doc.setLineDashPattern()
  }

  const blob = new Blob([doc.output("blob")], { type: "application/pdf" })
  window.open(URL.createObjectURL(blob), "_blank")
}
